/*
 * File: PostChargesModel.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: The data model representing the whole post charge model sent to the server
 */
import {
  types,
  flow,
  getSnapshot,
  applySnapshot,
  Instance,
  cast
} from "mobx-state-tree";
import ChargeModel from "./ChargeModel";
import PostChargeApi from "./PostChargePostApi";
import DateFormatter from "utils/DateFormatter";
import { DiagnosisModel } from "./UISearchStore";

const PostChargesModel = types
  .model({
    charges: types.optional(types.array(ChargeModel), []),
    providerId: types.maybe(types.number),
    orderNotes: types.optional(types.string, ""),
    procedureDate: types.optional(types.Date, new Date())
  })
  .views(self => ({
    findCharge(charge: Instance<typeof ChargeModel>) {
      const indexOfCharge = self.charges.indexOf(charge);
      return self.charges[indexOfCharge];
    },
    get hasCharges() {
      return self.charges.length > 0;
    },
    validate() {
      if (!self.providerId) {
        return "Provider is required";
      }
      //Procedure Date is pulled in once form is loaded, and date picker should handle any date errors, but adding this in case blank date is not caught.
      if (!self.procedureDate) return "Procedure Date is required";
      return "";
    },
    diagnosisIsIncludedOnCharge: (
      diagnosis: Instance<typeof DiagnosisModel>
    ) => {
      let dxIncluded = false;
      self.charges.forEach(charge => {
        charge.diagnosisModels.forEach(diagnosisModel => {
          if (diagnosis.pointer === diagnosisModel.pointer) {
            dxIncluded = true;
          }
        });
      });
      return dxIncluded;
    }
  }))
  .actions(self => ({
    setProviderId(val: string) {
      self.providerId = parseInt(val);
    },
    setProcedureDate(procedureDate: Date) {
      self.procedureDate = procedureDate;
    },
    addCharge(newCharge: Instance<typeof ChargeModel>) {
      self.charges.push(newCharge);
    },
    deleteCharge: (chargeToDelete: Instance<typeof ChargeModel>) => {
      const indexToDelete = self.charges.indexOf(chargeToDelete);
      if (indexToDelete > -1) {
        self.charges.splice(indexToDelete, 1);
      }
    },
    editCharge: (
      oldModel: Instance<typeof ChargeModel>,
      editedModel: Instance<typeof ChargeModel>
    ) => {
      applySnapshot(oldModel, getSnapshot(editedModel));
    },
    postCharge: flow(function*(patientId) {
      try {
        const procedureDateString = DateFormatter.forApi(self.procedureDate);
        self.charges.forEach(charge => {
          charge.diagnosisIds = cast(charge.getDiagnosisIds());
          charge.procedureDate = procedureDateString;
        });
        return yield PostChargeApi.postCharge(patientId, getSnapshot(self));
      } catch (error) {
        console.error(error);
        //Hopefully we get actual error codes back sometime, creating a fake error code for now
        return 404;
      }
    })
  }));
export default PostChargesModel;
